import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Typography, Paper, Grid, Card, CardContent, Button, 
  TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Alert, CircularProgress, Divider, Tooltip, Chip, MenuItem, Select,
  FormControl, InputLabel, InputAdornment, List, ListItem, ListItemText,
  ListItemIcon, ListItemSecondaryAction, Tab, Tabs, Accordion, AccordionSummary,
  AccordionDetails, Fade, Grow, LinearProgress, Switch, Slider, FormControlLabel,
  Checkbox, Collapse, Skeleton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ingestFile, getFiles, deleteFile, crawlWebsite, getUploadStatus,
  setApiKey, setApiEndpoint
} from '../../api/services/agent-studio/AgentFeatures';
import { 
  CloudUpload as CloudUploadIcon, Description as DescriptionIcon, 
  Delete as DeleteIcon, Visibility as VisibilityIcon, 
  Language as LanguageIcon, Check as CheckIcon, 
  Warning as WarningIcon, Error as ErrorIcon, 
  Refresh as RefreshIcon, FileCopy as FileCopyIcon, 
  Launch as LaunchIcon, ExpandMore as ExpandMoreIcon,
  CloudQueue as CloudQueueIcon, ArrowBack as ArrowBackIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const RAGUpload = ({ agentId, apiKey, endpoint }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [crawlDialogOpen, setCrawlDialogOpen] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [crawlUrlInput, setCrawlUrlInput] = useState('');
  const [maxPage, setMaxPage] = useState(100);
  const [maxDepth, setMaxDepth] = useState(3);
  const [crawlSubDomain, setCrawlSubDomain] = useState(true);
  const [fetchFiles, setFetchFiles] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [statusInterval, setStatusInterval] = useState(null);
  const [uploadStatusInterval, setUploadStatusInterval] = useState(null);
  const [currentStatus, setCurrentStatus] = useState('idle');
  const { serverId } = useParams();
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const activeUploadRef = useRef(null);

  const isValidUrl = crawlUrlInput.trim() === '' || crawlUrlInput.startsWith('https://');

  const getStatusInfo = () => {
    switch (currentStatus) {
      case 'idle':
        return { label: 'Ready', color: 'success' };
      case 'loading':
        return { label: 'Loading Documents', color: 'info' };
      case 'uploading':
        return { label: 'Uploading Files', color: 'warning' };
      case 'processing':
        return { label: 'Processing Files', color: 'info' };
      case 'indexing':
        return { label: 'Indexing Files', color: 'info' };
      case 'crawling':
        return { label: 'Crawling Website', color: 'info' };
      case 'completed':
        return { label: 'Operation Completed', color: 'success' };
      case 'failed':
        return { label: 'Operation Failed', color: 'error' };
      default:
        return { label: 'Ready', color: 'success' };
    }
  };

  useEffect(() => {
    if (apiKey) {
      setApiKey(apiKey);
      if (endpoint) {
        setApiEndpoint(endpoint);
      }
      fetchDocuments();
    } else {
      setError('No API key available for this agent');
      setLoading(false);
    }
  }, [apiKey, endpoint]);

  useEffect(() => {
    if (!apiKey || !endpoint) return;
    const stored = localStorage.getItem('rag_active_upload');
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed.agentId !== agentId || parsed.endpoint !== endpoint) return;
      activeUploadRef.current = parsed;
      if (parsed.type === 'file') {
        setUploading(true);
        setCurrentStatus('processing');
        startUploadPolling(parsed.uploadId, parsed.totalFiles || 0);
      } else if (parsed.type === 'crawl') {
        setCrawling(true);
        setCurrentStatus('crawling');
        startCrawlPolling(parsed.uploadId);
      }
    } catch (e) {
      localStorage.removeItem('rag_active_upload');
    }
  }, [agentId, apiKey, endpoint]);

  useEffect(() => {
    let refreshInterval;
    
    if (apiKey && documents.length > 0) {
      const hasProcessingDocs = documents.some(doc => 
        doc.status === 'preprocessing' || 
        doc.status === 'processing' || 
        doc.status === 'indexing'
      );
      
      const allComplete = documents.every(doc => 
        doc.status === 'complete' || doc.status === 'failed'
      );
      
      if (hasProcessingDocs && !allComplete) {
        refreshInterval = setInterval(() => {
          fetchDocuments();
        }, 4000);
      } else if (allComplete && documents.length > 0) {
        console.log('All documents processed. Auto-refresh stopped.');
      }
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [documents, apiKey]);

  useEffect(() => {
    return () => {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
      if (uploadStatusInterval) {
        clearInterval(uploadStatusInterval);
      }
    };
  }, [statusInterval, uploadStatusInterval]);

  const startUploadPolling = (uploadId, totalFiles) => {
    if (uploadStatusInterval) {
      clearInterval(uploadStatusInterval);
    }
    const interval = setInterval(async () => {
      try {
        const statusResponse = await getUploadStatus(uploadId, apiKey, endpoint);
        const status = statusResponse.data || statusResponse;
        setUploadStatus({
          uploadId,
          status: status.status || 'processing',
          message: status.message || 'Processing files...',
          progress: status.progress || 0,
          files_processed: status.files_processed || 0,
          total_files: status.total_files || totalFiles
        });
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          setUploadStatusInterval(null);
          setUploading(false);
          localStorage.removeItem('rag_active_upload');
          activeUploadRef.current = null;
          if (status.status === 'completed') {
            setSuccess(true);
            setCurrentStatus('completed');
            fetchDocuments();
            setTimeout(() => {
              setUploadStatus(null);
              setCurrentStatus('idle');
            }, 3000);
          } else {
            setError(status.message || 'Upload failed');
            setCurrentStatus('failed');
            setTimeout(() => {
              setCurrentStatus('idle');
            }, 5000);
          }
        }
      } catch (err) {
        console.error('Error checking upload status:', err);
      }
    }, 1500);
    setUploadStatusInterval(interval);
  };

  const startCrawlPolling = (uploadId) => {
    if (statusInterval) {
      clearInterval(statusInterval);
    }
    const interval = setInterval(async () => {
      try {
        const statusResponse = await getUploadStatus(uploadId, apiKey, endpoint);
        const status = statusResponse.data || statusResponse;
        setCrawlStatus({
          uploadId,
          status: status.status || 'processing',
          message: status.message || 'Processing...',
          progress: status.progress || 0,
          pages_crawled: status.pages_crawled || 0,
          pages_processed: status.pages_processed || 0,
          total_pages: status.total_pages || 0,
          current_url: status.current_url || '',
          errors: status.errors || []
        });
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          setStatusInterval(null);
          setCrawling(false);
          localStorage.removeItem('rag_active_upload');
          activeUploadRef.current = null;
          if (status.status === 'completed') {
            setSuccess(true);
            setCurrentStatus('completed');
            fetchDocuments();
            setTimeout(() => {
              setCrawlDialogOpen(false);
              setCrawlStatus(null);
              setCurrentStatus('idle');
            }, 3000);
          } else {
            setError(status.message || 'Crawling failed');
            setCurrentStatus('failed');
            setTimeout(() => {
              setCrawlStatus(null);
              setCurrentStatus('idle');
            }, 5000);
          }
        }
      } catch (err) {
        console.error('Error checking crawl status:', err);
        if (err.message && (err.message.includes('404') || err.message.includes('not found'))) {
          clearInterval(interval);
          setStatusInterval(null);
          setCrawling(false);
          setError('Crawl status not found. The crawl may have completed or failed.');
          setCurrentStatus('failed');
          setTimeout(() => {
            setCrawlStatus(null);
            setCurrentStatus('idle');
          }, 3000);
        }
      }
    }, 1500);
    setStatusInterval(interval);
  };

  const fetchDocuments = async () => {
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }

      setTimeout(() => setLoading(true), 100);
      setTimeout(() => setCurrentStatus('loading'), 100);
      let allFiles = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await getFiles(apiKey, currentPage, endpoint);
        const files = response.data.files;
        
        if (!files || files.length === 0) {
          hasMorePages = false;
        } else {
          allFiles = [...allFiles, ...files];
          currentPage++;
        }
      }

      setDocuments(allFiles.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size_bytes,
        type: file.mime_type === 'text/html' ? 'html' : 
              (file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : 'unknown'),
        date: new Date(file.created_at).toLocaleDateString(),
        status: file.status,
        filename: file.filename,
        mime_type: file.mime_type,
        updated_at: file.updated_at,
        domain: file.domain,
        md_filename: file.md_filename
      })));
    } catch (err) {
      setError('Failed to fetch documents');
    } finally {
      setTimeout(() => setLoading(false), 200);
      setTimeout(() => setCurrentStatus('idle'), 300);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError(null);
    setSuccess(false);
    setUploadStatus(null);
    setCurrentStatus('uploading');

    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }

      const validTypes = [
        '.pdf', '.txt', '.doc', '.docx', '.xlsx', '.pptx', '.md', '.zip',
        '.adoc', '.asciidoc', '.html', '.xhtml', '.htm', '.csv',
        '.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.webp'
      ];

      const invalidFiles = files.filter(file => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return !validTypes.includes(extension);
      });

      if (invalidFiles.length > 0) {
        throw new Error('Some files have invalid types. Please check supported file formats.');
      }

      const uploadResponse = await ingestFile(files, apiKey, endpoint);
      const uploadId = uploadResponse.data?.upload_id || uploadResponse.upload_id || uploadResponse.data?.id;

      if (uploadId) {
        const stored = {
          uploadId,
          type: 'file',
          agentId,
          endpoint,
          totalFiles: files.length
        };
        localStorage.setItem('rag_active_upload', JSON.stringify(stored));
        activeUploadRef.current = stored;
        setUploadStatus({
          uploadId,
          status: 'started',
          message: 'Upload started...',
          progress: 0,
          files_processed: 0,
          total_files: files.length
        });
        setCurrentStatus('processing');
        startUploadPolling(uploadId, files.length);
      } else {
        await fetchDocuments();
        setSuccess(true);
        setUploading(false);
        setCurrentStatus('completed');
        setTimeout(() => {
          setCurrentStatus('idle');
        }, 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload files. Please try again.');
      setUploading(false);
      setCurrentStatus('failed');
      setTimeout(() => {
        setCurrentStatus('idle');
      }, 5000);
    } finally {
      event.target.value = '';
    }
  };

  const handleCrawlSubmit = async () => {
    if (!crawlUrlInput.trim() || !isValidUrl) {
      setError('Please enter a valid HTTPS URL');
      return;
    }

    setCrawling(true);
    setError(null);
    setCrawlStatus(null);
    setCurrentStatus('crawling');

    const crawlData = {
      url: crawlUrlInput.trim(),
      max_page: maxPage,
      max_depth: maxDepth,
      crawl_sub_domain: crawlSubDomain,
      fetch_files: fetchFiles
    };

    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }

      const response = await crawlWebsite(crawlData, apiKey, endpoint);
      const uploadId = response.data?.upload_id || response.upload_id;
      
      if (uploadId) {
        const stored = {
          uploadId,
          type: 'crawl',
          agentId,
          endpoint
        };
        localStorage.setItem('rag_active_upload', JSON.stringify(stored));
        activeUploadRef.current = stored;
        setCrawlStatus({
          uploadId,
          status: 'started',
          message: 'Crawling started...'
        });

        startCrawlPolling(uploadId);
      }

      setCrawlDialogOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to start web crawling. Please try again.');
      setCrawling(false);
      setCurrentStatus('failed');
      setTimeout(() => {
        setCurrentStatus('idle');
      }, 5000);
    }
  };

  const handleDelete = async (docId) => {
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }
      
      await deleteFile(docId, apiKey, endpoint);
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
    } catch (err) {
      setError(err.message || 'Failed to delete document');
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      pdf: 'error',
      txt: 'success',
      docx: 'primary',
      xlsx: 'primary',
      pptx: 'primary',
      md: 'warning',
      adoc: 'warning',
      asciidoc: 'warning',
      html: 'info',
      xhtml: 'info',
      htm: 'info',
      csv: 'success',
      zip: 'secondary',
      png: 'info',
      jpg: 'info',
      jpeg: 'info',
      tiff: 'info',
      tif: 'info',
      bmp: 'info',
      webp: 'info',
      unknown: 'default'
    };
    return colors[type] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      complete: 'success',
      preprocessing: 'warning',
      processing: 'info',
      indexing: 'info',
      failed: 'error',
      pending: 'default'
    };
    return colors[status] || 'default';
  };

  const handleCloseCrawlDialog = () => {
    setCrawlDialogOpen(false);
    setCrawlUrlInput('');
    setMaxPage(100);
    setMaxDepth(3);
    setCrawlSubDomain(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          RAG Document Management
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={`Status: ${getStatusInfo().label}`}
            color={getStatusInfo().color}
            variant="outlined"
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 2
              }
            }}
          />
          <Fade in={uploading || crawling || loading} timeout={300}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress 
                size={20} 
                sx={{ 
                  transition: 'all 0.3s ease-in-out',
                  '&.MuiCircularProgress-root': {
                    animationDuration: '1.5s'
                  }
                }}
              />
            </Box>
          </Fade>
        </Box>
      </Box>
      
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
            <input
              accept=".pdf,.txt,.doc,.docx,.xlsx,.pptx,.md,.zip,.adoc,.asciidoc,.html,.xhtml,.htm,.csv,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleFileUpload}
              multiple
            />
            <label htmlFor="raised-button-file">
                          <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={uploading || !apiKey}
              size="large"
              sx={{
                transition: 'all 0.3s ease-in-out',
                '&:hover:not(:disabled)': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                },
                '&:active': {
                  transform: 'translateY(0px)'
                }
              }}
            >
              Upload Documents
            </Button>
            </label>

            <Button
              variant="outlined"
              startIcon={<LanguageIcon />}
              onClick={() => setCrawlDialogOpen(true)}
              disabled={crawling || !apiKey}
              size="large"
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                transition: 'all 0.3s ease-in-out',
                '&:hover:not(:disabled)': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(64, 196, 99, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: 2
                },
                '&:active': {
                  transform: 'translateY(0px)'
                }
              }}
            >
              Crawl Website
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Upload documents (PDF, DOCX, XLSX, PPTX, MD, HTML, CSV, Images) or crawl websites for RAG processing. No file size limit.
          </Typography>
        </Box>

        <Fade in={!!uploadStatus} timeout={500}>
          <Box>
            {uploadStatus && (
              <Alert 
                severity={uploadStatus.status === 'failed' ? 'error' : 'info'} 
                sx={{ mb: 2, transition: 'all 0.3s ease-in-out' }}
              >
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Document Upload: {uploadStatus.message}
                  </Typography>
                  <Fade in={uploadStatus.files_processed > 0} timeout={300}>
                    <Box>
                      {uploadStatus.files_processed > 0 && (
                        <Typography variant="body2">
                          Files processed: {uploadStatus.files_processed}/{uploadStatus.total_files}
                        </Typography>
                      )}
                    </Box>
                  </Fade>
                  <Fade in={uploadStatus.progress > 0} timeout={300}>
                    <Box>
                      {uploadStatus.progress > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">Progress</Typography>
                            <Typography variant="body2">{uploadStatus.progress}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={uploadStatus.progress} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              transition: 'all 0.3s ease-in-out'
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Fade>
                </Box>
              </Alert>
            )}
          </Box>
        </Fade>

        <Fade in={!!crawlStatus} timeout={500}>
          <Box>
            {crawlStatus && (
              <Alert 
                severity={crawlStatus.status === 'failed' ? 'error' : 'info'} 
                sx={{ mb: 2, transition: 'all 0.3s ease-in-out' }}
              >
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Website Crawl: {crawlStatus.message}
                  </Typography>
                  <Fade in={crawlStatus.pages_crawled > 0} timeout={300}>
                    <Box>
                      {crawlStatus.pages_crawled > 0 && (
                        <Typography variant="body2">
                          Pages crawled: {crawlStatus.pages_crawled}
                        </Typography>
                      )}
                    </Box>
                  </Fade>
                  <Fade in={crawlStatus.pages_processed > 0 && crawlStatus.total_pages > 0} timeout={300}>
                    <Box>
                      {crawlStatus.pages_processed > 0 && crawlStatus.total_pages > 0 && (
                        <Typography variant="body2">
                          Pages processed: {crawlStatus.pages_processed}/{crawlStatus.total_pages}
                        </Typography>
                      )}
                    </Box>
                  </Fade>
                  <Fade in={!!crawlStatus.current_url} timeout={300}>
                    <Box>
                      {crawlStatus.current_url && (
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          Current: {crawlStatus.current_url}
                        </Typography>
                      )}
                    </Box>
                  </Fade>
                  <Fade in={crawlStatus.progress > 0} timeout={300}>
                    <Box>
                      {crawlStatus.progress > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">Progress</Typography>
                            <Typography variant="body2">{crawlStatus.progress}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={crawlStatus.progress} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              transition: 'all 0.3s ease-in-out'
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Fade>
                  <Fade in={crawlStatus.errors && crawlStatus.errors.length > 0} timeout={300}>
                    <Box>
                      {crawlStatus.errors && crawlStatus.errors.length > 0 && (
                        <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
                          Errors encountered: {crawlStatus.errors.length}
                        </Typography>
                      )}
                    </Box>
                  </Fade>
                </Box>
              </Alert>
            )}
          </Box>
        </Fade>

        <Fade in={uploading} timeout={400}>
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ mt: 1, transition: 'all 0.3s ease-in-out' }}>Uploading...</Typography>
          </Box>
        </Fade>

        <Fade in={crawling} timeout={400}>
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ mt: 1, transition: 'all 0.3s ease-in-out' }}>Crawling website...</Typography>
          </Box>
        </Fade>

        <Fade in={loading} timeout={400}>
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ mt: 1, transition: 'all 0.3s ease-in-out' }}>Loading documents...</Typography>
          </Box>
        </Fade>

        <Fade in={!!error} timeout={400}>
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2, transition: 'all 0.3s ease-in-out' }}>
                {error}
              </Alert>
            )}
          </Box>
        </Fade>

        <Fade in={success} timeout={400}>
          <Box>
            {success && (
              <Alert severity="success" sx={{ mb: 2, transition: 'all 0.3s ease-in-out' }}>
                Operation completed successfully!
              </Alert>
            )}
          </Box>
        </Fade>

        {loading && documents.length === 0 ? (
          <Box>
            {[...Array(3)].map((_, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Box>
        ) : (
          <List>
            {documents.map((doc, index) => (
              <Fade in={true} timeout={300 + index * 100} key={doc.id}>
                <ListItem
                  secondaryAction={
                    <Box>
                      <Tooltip title="Preview">
                        <IconButton 
                          edge="end" 
                          onClick={() => {
                            setSelectedDoc(doc);
                            setPreviewOpen(true);
                          }}
                          sx={{ 
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': { 
                              transform: 'scale(1.1)',
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          edge="end" 
                          onClick={() => handleDelete(doc.id)}
                          sx={{ 
                            ml: 1,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': { 
                              transform: 'scale(1.1)',
                              bgcolor: 'error.light',
                              color: 'error.contrastText'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                  sx={{ 
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': { 
                      bgcolor: 'action.hover',
                      transform: 'translateX(4px)',
                      boxShadow: '2px 2px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">
                      {doc.name}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                    <Chip 
                      label={doc.status.toUpperCase()} 
                      size="small" 
                      color={getStatusColor(doc.status)}
                      sx={{ 
                        fontSize: '0.7rem', 
                        height: '20px',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: 1
                        }
                      }}
                    />
                    <Chip 
                      label={doc.type.toUpperCase()} 
                      size="small" 
                      color={getTypeColor(doc.type)}
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.7rem', 
                        height: '20px',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: 1
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {doc.size ? `${Math.round(doc.size / 1024)} KB` : 'Unknown size'} • {doc.date}
                    </Typography>
                    {doc.domain && doc.domain !== 'main' && (
                      <Chip 
                        label={doc.domain} 
                        size="small" 
                        color="secondary"
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.7rem', 
                          height: '20px',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 1
                          }
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          </Fade>
        ))}
          </List>
        )}

        {documents.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No documents uploaded yet
            </Typography>
          </Box>
        )}
        
        {documents.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              {documents.length} document{documents.length !== 1 ? 's' : ''} • 
              {documents.filter(doc => doc.status === 'complete').length} complete • 
              {documents.filter(doc => doc.status === 'preprocessing' || doc.status === 'processing' || doc.status === 'indexing').length} processing
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchDocuments}
              disabled={loading}
              sx={{ 
                ml: 2,
                transition: 'all 0.3s ease-in-out',
                '&:hover:not(:disabled)': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                },
                '&:active': {
                  transform: 'translateY(0px)'
                }
              }}
            >
              Refresh
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Document Preview: {selectedDoc?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">
              Preview not available for this file type
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={crawlDialogOpen} 
        onClose={handleCloseCrawlDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '500px',
            maxWidth: '90vw'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">Crawl Website</Typography>
          <IconButton onClick={handleCloseCrawlDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the URL of a website to crawl and index its content
          </Typography>
          
          <TextField
            label="Website URL"
            placeholder="https://example.com"
            value={crawlUrlInput}
            onChange={e => setCrawlUrlInput(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            disabled={crawling}
            error={crawlUrlInput.trim() !== '' && !isValidUrl}
            helperText={crawlUrlInput.trim() !== '' && !isValidUrl ? "URL must start with https://" : ""}
          />
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Max Pages (Default: 100)
            </Typography>
            <Slider
              value={maxPage}
              onChange={(e, newValue) => setMaxPage(newValue)}
              min={10}
              max={500}
              step={10}
              valueLabelDisplay="auto"
              disabled={crawling}
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Max Depth (Default: 3)
            </Typography>
            <Slider
              value={maxDepth}
              onChange={(e, newValue) => setMaxDepth(newValue)}
              min={1}
              max={10}
              step={1}
              valueLabelDisplay="auto"
              disabled={crawling}
            />
          </Box>
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={crawlSubDomain}
                onChange={(e) => setCrawlSubDomain(e.target.checked)}
                disabled={crawling}
              />
            }
            label="Crawl Subdomains (Default: Yes)"
          />
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={fetchFiles}
                onChange={(e) => setFetchFiles(e.target.checked)}
                disabled={crawling}
              />
            }
            label="Fetch Files (Download PDF and other files)"
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseCrawlDialog} 
            variant="outlined"
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCrawlSubmit} 
            variant="contained"
            disabled={crawling || !crawlUrlInput.trim() || !isValidUrl}
            sx={{ borderRadius: 1.5 }}
          >
            {crawling ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Crawling...
              </>
            ) : (
              'Start Crawling'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RAGUpload;
